import {
  CondenserType,
  ConfigurationType,
  LevelType,
  LineSetType,
  ZoneType
} from 'data/types';
import {
  ExportedConfigurationJson,
  ExportedZoneSpaceItemJson
} from 'types/structure-json';
import { resolveZoneType } from 'utils/resolve-zone-type';

function buildSpaceLevelMap(levels: LevelType[]): Map<string, string> {
  const spaceLevelMap = new Map<string, string>();
  levels.forEach((level) => {
    level.sections.forEach((section) => {
      section.spaces.forEach((space) => {
        spaceLevelMap.set(space.uniqueId, level.uniqueId);
      });
    });
  });
  return spaceLevelMap;
}

function deriveConfigurationType(
  condenserTypeCodes: ConfigurationType['type'][]
): ConfigurationType['type'] {
  const hasHybrid = condenserTypeCodes.includes('hybrid');
  const hasDucted = condenserTypeCodes.includes('ducted');
  const hasDuctless = condenserTypeCodes.includes('ductless');
  if (hasHybrid || (hasDucted && hasDuctless)) {
    return 'hybrid';
  }
  if (hasDucted) {
    return 'ducted';
  }
  return 'ductless';
}

function restoreZones(exported: ExportedConfigurationJson): ZoneType[] {
  return exported.zones.map((zone) => {
    const airHandler = exported.airHandlers.find(
      (ah) => ah.zoneUniqueId === zone.uniqueId
    );
    const condenser = exported.condensers.find(
      (c) => c.uniqueId === zone.condenserUniqueId
    );

    const itemsSource: ExportedZoneSpaceItemJson[] =
      zone.spaceItems && zone.spaceItems.length > 0
        ? zone.spaceItems
        : zone.spaceUniqueIds.map((structureItemId) => ({
            structureItemId,
            registerOverride: null,
            additionalSupplyRuns: []
          }));

    return {
      uniqueId: zone.uniqueId,
      name: zone.label,
      type: resolveZoneType({
        persistedType: zone.zoneTypeCode,
        condenserTypeCode: condenser?.condenserTypeCode,
        airHandlerTypeCode: airHandler?.airHandlerTypeCode
      }),
      ...(zone.existingSystemUniqueId
        ? { existingSystemId: zone.existingSystemUniqueId }
        : {}),
      zoneItems: itemsSource.map((item) => ({
        uniqueId: item.uniqueId ?? item.structureItemId,
        structureItemId: item.structureItemId,
        name: '',
        BTUs: { cooling: 0, heating: 0 },
        originalBTUs: { cooling: 0, heating: 0 },
        ...(item.isPartition ? { isPartition: true } : {}),
        ...(item.isSeparateSystem ? { isSeparateSystem: true } : {}),
        ...(item.registerOverride
          ? { registerOverride: item.registerOverride }
          : {}),
        ...(item.additionalSupplyRuns && item.additionalSupplyRuns.length > 0
          ? { additionalSupplyRuns: item.additionalSupplyRuns }
          : {}),
        ...(item.supplyRunPoints && item.supplyRunPoints.length > 0
          ? { supplyRunPoints: item.supplyRunPoints }
          : {})
      })),
      valid: zone.valid,
      modified: zone.modified,
      ...(zone.returnOverride ? { returnOverride: zone.returnOverride } : {}),
      ...(zone.additionalReturnRuns && zone.additionalReturnRuns.length > 0
        ? { additionalReturnRuns: zone.additionalReturnRuns }
        : {}),
      ...(zone.returnRunPoints && zone.returnRunPoints.length > 0
        ? { returnRunPoints: zone.returnRunPoints }
        : {}),
      airHandler: airHandler
        ? {
            uniqueId: airHandler.uniqueId,
            airHandlerTypeCode: airHandler.airHandlerTypeCode,
            positionX: airHandler.positionX,
            positionY: airHandler.positionY,
            valid: airHandler.valid,
            modified: airHandler.modified,
            ...(airHandler.userPositioned != null
              ? { userPositioned: !!airHandler.userPositioned }
              : {}),
            ...(airHandler.concealedLocation != null
              ? { concealedLocation: airHandler.concealedLocation }
              : {})
          }
        : undefined
    };
  });
}

function restoreCondensers(
  exported: ExportedConfigurationJson,
  zones: ZoneType[]
): CondenserType[] {
  return exported.condensers.map((condenser) => {
    const condenserZones = zones.filter((zone) => {
      const exportedZone = exported.zones.find(
        (z) => z.uniqueId === zone.uniqueId
      );
      return exportedZone?.condenserUniqueId === condenser.uniqueId;
    });

    const position =
      condenser.positionX !== null && condenser.positionY !== null
        ? { x: condenser.positionX, y: condenser.positionY }
        : undefined;

    return {
      uniqueId: condenser.uniqueId,
      levelUniqueId: condenser.levelUniqueId,
      type: condenser.condenserTypeCode,
      name: condenser.label,
      zones: condenserZones,
      heightOffGround: condenser.heightOffGround,
      validSystems: [],
      validDuctedSystems: [],
      ductedAccessories: [],
      selectedAccessories: [],
      accessoriesCompleted: [],
      accessoriesNotNeeded: [],
      noAccessoriesNeeded: condenser.noAccessoriesNeeded,
      valid: condenser.valid,
      modified: condenser.modified,
      position,
      placementType: condenser.placementType
    };
  });
}

function restoreLineSets(
  exported: ExportedConfigurationJson,
  spaceLevelMap: Map<string, string>
): LineSetType[] {
  return exported.lineSets.map((lineSet) => {
    const condenser = exported.condensers.find(
      (c) => c.uniqueId === lineSet.condenserUniqueId
    );
    const airHandler = exported.airHandlers.find(
      (ah) => ah.uniqueId === lineSet.airHandlerUniqueId
    );
    const airHandlerZone = exported.zones.find(
      (zone) => zone.uniqueId === airHandler?.zoneUniqueId
    );

    const firstSpaceUniqueId = airHandlerZone?.spaceUniqueIds[0];
    const airHandlerLevelUniqueId = firstSpaceUniqueId
      ? spaceLevelMap.get(firstSpaceUniqueId)
      : undefined;
    const condenserLevelUniqueId = condenser?.levelUniqueId;
    const isCrossFloor =
      !!condenserLevelUniqueId &&
      !!airHandlerLevelUniqueId &&
      condenserLevelUniqueId !== airHandlerLevelUniqueId;

    return {
      ...lineSet,
      airHandlerTypeCode: airHandler?.airHandlerTypeCode,
      condenserLevelUniqueId,
      airHandlerLevelUniqueId,
      isCrossFloor
    };
  });
}

export function restoreConfigurationsFromExport(
  exportedConfigurations: ExportedConfigurationJson[],
  levels: LevelType[]
): ConfigurationType[] {
  const spaceLevelMap = buildSpaceLevelMap(levels);

  return exportedConfigurations.map((exported) => {
    const zones = restoreZones(exported);

    return {
      uniqueId: exported.uniqueId,
      name: exported.label,
      type: deriveConfigurationType(
        exported.condensers.map((condenser) => condenser.condenserTypeCode)
      ),
      condensers: restoreCondensers(exported, zones),
      valid: exported.valid,
      modified: exported.modified,
      aiGenerated: exported.aiGenerated,
      lineSets: restoreLineSets(exported, spaceLevelMap)
    };
  });
}
